import { computed, Injectable, signal, Signal } from '@angular/core';
import { IStatusIndicatorService, StatusIndicatorOptions, StatusState } from './status-indicator.types';

/**
 * Singleton service that manages connectivity state based on navigator.onLine and window online/offline events.
 *
 * Features:
 *
 * - Debounced network event handling to prevent flicker
 * - Optional heartbeat check for server reachability confirmation
 * - Read-only isOnline signal exposed to consumers
 * - Configurable debounce time and initial state
 */
@Injectable({ providedIn: 'root' })
export class StatusIndicatorService implements IStatusIndicatorService {
  private debounceMs = 500;
  private initialState = typeof navigator !== 'undefined' ? navigator.onLine : true;
  private heartbeatUrl: string | null = null;
  private heartbeatTimeoutMs = 1000;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private isListening = false;
  /** Timestamp of the most recent navigator-driven transition; probes started before it are stale. */
  private lastNavigatorEventAt = 0;
  private readonly handleOnline = (): void => this.queueStateUpdate(true);
  private readonly handleOffline = (): void => this.queueStateUpdate(false);

  /** Internal state signal */
  private readonly stateSignal = signal<StatusState>({
    isOnline: this.initialState,
    lastChangedAt: Date.now(),
    source: 'initial',
  });

  /** Public read-only signal for isOnline status */
  readonly isOnline: Signal<boolean> = computed(() => this.stateSignal().isOnline);

  /** Public read-only signal for full state */
  readonly state: Signal<StatusState> = this.stateSignal.asReadonly();

  constructor() {
    // Initialize with current browser state (not hardcoded true)
    this.updateState(this.initialState, 'initial');
    if (typeof window !== 'undefined') {
      this.start();
    }
  }

  /** Configure service options */
  configure(options: StatusIndicatorOptions): void {
    if (options.debounceMs !== undefined) {
      this.debounceMs = options.debounceMs;
    }
    if (options.initialState !== undefined) {
      // Handle initialState: can be boolean or string
      const isOnline =
        typeof options.initialState === 'boolean' ? options.initialState : options.initialState === 'online';

      this.initialState = isOnline;
      this.updateState(isOnline, 'initial');
    }
  }

  /** Start listening to network events */
  start(): void {
    if (this.isListening) {
      return;
    }

    this.isListening = true;
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  /** Stop listening to network events */
  stop(): void {
    if (!this.isListening) {
      return;
    }

    this.isListening = false;
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);

    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  /** Force an immediate connectivity check */
  async forceCheck(): Promise<StatusState> {
    const navigatorOnline = navigator.onLine;
    this.lastNavigatorEventAt = Date.now();
    this.updateState(navigatorOnline, 'navigator');
    return this.stateSignal();
  }

  /**
   * Report the result of an external connectivity probe (e.g. a health-endpoint fetch). navigator.onLine only reflects
   * the network adapter, not real reachability — a captive portal, VPN/localhost dev server being down, or DevTools
   * request-blocking-only offline simulation can leave navigator.onLine `true` while requests actually fail. Callers
   * with a real probe (e.g. checkNetworkOnline) should report the result here so the indicator matches.
   *
   * Pass `observedAt` (the time the probe started) so results that predate the latest navigator transition are dropped.
   */
  reportProbeResult(isOnline: boolean, observedAt: number = Date.now()): void {
    if (observedAt < this.lastNavigatorEventAt) {
      return;
    }
    if (this.stateSignal().isOnline === isOnline) {
      return;
    }
    this.updateState(isOnline, 'probe');
  }

  /** Queue a state update with debounce to avoid flicker */
  private queueStateUpdate(online: boolean): void {
    this.lastNavigatorEventAt = Date.now();
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    // Network disconnection is critical; transition immediately without debounce
    if (!online) {
      this.updateState(false, 'navigator');
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.updateState(true, 'navigator');
      this.debounceTimer = null;
    }, this.debounceMs);
  }

  /** Update the internal state signal */
  private updateState(isOnline: boolean, source: 'navigator' | 'initial' | 'probe'): void {
    this.stateSignal.set({
      isOnline,
      lastChangedAt: Date.now(),
      source,
    });
  }
}
